import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  InputNumber,
  Row,
  Spin,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import { apiService, type AccountLimitsResponse } from "@/services/api";

const { Title, Paragraph, Text } = Typography;

type FormValues = {
  maxDevices: number;
  maxAdultProfiles: number;
  maxKidProfiles: number;
};

/**
 * Limites par compte abonné (UX-01).
 *
 * Deux règles distinctes, réglées ensemble :
 * - appareils connectés en même temps, appliqué par Keycloak : au-delà, la
 *   session la plus ancienne est fermée ;
 * - profils adultes et enfants par compte, appliqué par l'API mobile.
 */
export const AccountLimitsPage: React.FC = () => {
  const [form] = Form.useForm<FormValues>();
  const [data, setData] = useState<AccountLimitsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiService.getAccountLimits();
      setData(response);
      form.setFieldsValue({
        maxDevices: response.sessions.maxDevices ?? response.profiles?.maxDevices ?? 3,
        maxAdultProfiles: response.profiles?.maxAdultProfiles ?? 3,
        maxKidProfiles: response.profiles?.maxKidProfiles ?? 1,
      });
    } catch (e: any) {
      setError(e?.response?.data?.message || "Lecture des limites impossible");
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (values: FormValues) => {
    setSaving(true);
    setError(null);
    try {
      const response = await apiService.updateAccountLimits(values);
      setData(response);
      message.success("Limites enregistrées");
    } catch (e: any) {
      const body = e?.response?.data;
      setError(body?.message || body?.profilesProblem || "Enregistrement impossible");
      if (body?.sessions) {
        setData((previous) => (previous ? { ...previous, sessions: body.sessions } : previous));
      }
    } finally {
      setSaving(false);
    }
  };

  const sessions = data?.sessions;
  const limiterReady = sessions?.installed === true;

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            Comptes & appareils
          </Title>
          <Paragraph type="secondary" style={{ margin: 0 }}>
            Ce que chaque abonnement autorise : appareils connectés en même temps et profils du foyer.
          </Paragraph>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Actualiser
          </Button>
        </Col>
      </Row>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      {sessions && !limiterReady && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Limite d'appareils non active dans Keycloak"
          description={
            <>
              {sessions.problem}
              <br />
              Tant que le limiteur n'est pas installé, l'enregistrement est refusé : une limite que personne
              n'applique serait trompeuse.
            </>
          }
        />
      )}

      <Spin spinning={loading}>
        <Row gutter={16}>
          <Col xs={24} lg={14}>
            <Card title="Limites">
              <Form form={form} layout="vertical" onFinish={save} requiredMark={false}>
                <Form.Item
                  label="Appareils connectés en même temps"
                  name="maxDevices"
                  extra="Au-delà, l'appareil connecté depuis le plus longtemps est déconnecté."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={10} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item
                  label="Profils adultes par compte"
                  name="maxAdultProfiles"
                  extra="Le titulaire compte pour un profil adulte."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={1} max={10} style={{ width: 160 }} />
                </Form.Item>
                <Form.Item
                  label="Profils enfant par compte"
                  name="maxKidProfiles"
                  extra="Contenus tout public uniquement. 0 désactive les profils enfant."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={5} style={{ width: 160 }} />
                </Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                  disabled={!limiterReady}
                >
                  Enregistrer
                </Button>
              </Form>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card title="État actuel">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="Limiteur Keycloak">
                  {limiterReady ? <Tag color="green">Actif</Tag> : <Tag color="orange">Non installé</Tag>}
                </Descriptions.Item>
                <Descriptions.Item label="Appareils (Keycloak)">{sessions?.maxDevices ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Au-delà">
                  {sessions?.behavior === "Terminate oldest session"
                    ? "Déconnexion du plus ancien"
                    : sessions?.behavior ?? "—"}
                </Descriptions.Item>
                <Descriptions.Item label="Profils adultes">{data?.profiles?.maxAdultProfiles ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Profils enfant">{data?.profiles?.maxKidProfiles ?? "—"}</Descriptions.Item>
                <Descriptions.Item label="Dernière modification">
                  {data?.profiles?.updatedAt ? (
                    <Text>
                      {new Date(data.profiles.updatedAt).toLocaleString("fr-FR")}
                      {data.profiles.updatedBy ? ` par ${data.profiles.updatedBy}` : ""}
                    </Text>
                  ) : (
                    "—"
                  )}
                </Descriptions.Item>
              </Descriptions>
              {data?.profilesProblem && <Alert type="warning" showIcon message={data.profilesProblem} />}
            </Card>
          </Col>
        </Row>
      </Spin>
    </div>
  );
};
